import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEmpty } from 'lodash'
import type { Moment } from 'moment-timezone'
import moment from 'moment-timezone'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import type { Contact, Group } from '../../../ContactsAndGroups/ContactsAndGroupsContext'
import type { AlertTypeId, Rule } from '../../_types'
import { convertAlertRulesIfApplicable } from '../../_utils/convertAlertRulesIfApplicable'
import { AlertsSettings } from '../../settings/alertSettings'

export type TVariables = NonNullable<
  routes.LegacyV3AlertsManagement.Request['legacyV3AlertsToCreate']
>[number]

export const getAlertVariablesForQuery = ({
  alertTypeId,
  contacts,
  coverageBlocks,
  coverageProperties,
  coverageStations,
  coverageType,
  endDate,
  frequencyMinutes,
  groups,
  insectId,
  name,
  notifyOwnerByEmail,
  notifyOwnerBySMS,
  rules,
  sleepMinutes,
  startDate,
}: {
  alertTypeId: AlertTypeId
  contacts: Contact[]
  coverageBlocks: string[]
  coverageProperties: number[]
  coverageStations: string[]
  coverageType: string
  endDate: Moment | null
  frequencyMinutes: number | null
  groups: Group[]
  insectId: number | null
  name: string
  notifyOwnerByEmail: boolean
  notifyOwnerBySMS: boolean
  rules: Rule[]
  sleepMinutes: number | null
  startDate: Moment | null
}): TVariables => {
  const { temperature: userTemperatureUnit } = userDetailsStore.getState()

  const contactsShaped: TVariables['contacts'] = contacts
    .filter(
      (
        c,
      ): c is Contact & {
        alertsContactMethods: NonNullable<TVariables['contacts']>[number]['contactMethods']
      } => !isEmpty(c.alertsContactMethods),
    )
    .map((c) => ({
      contactId: c.id,
      contactMethods: c.alertsContactMethods ?? [],
    }))

  const groupsShaped: TVariables['groups'] = groups
    .filter(
      (
        g,
      ): g is Group & {
        alertsContactMethods: NonNullable<TVariables['groups']>[number]['contactMethods']
      } => !isEmpty(g.alertsContactMethods),
    )
    .map((g) => ({
      groupId: g.id,
      contactMethods: g.alertsContactMethods ?? [],
    }))

  const variables: TVariables = {
    alertType: alertTypeId,
    blockIds: null,
    contacts: contactsShaped,
    groups: groupsShaped,
    endDate: null,
    frequencyMinutes: frequencyMinutes ?? AlertsSettings.frequencyMinutesDefault[alertTypeId],
    insectId: insectId ? +insectId : null,
    name,
    notifyOwnerByEmail,
    notifyOwnerBySMS,
    propertyIds: null,
    alertRules: rules.map((rule) => ({
      ...rule,
      value: String(
        convertAlertRulesIfApplicable({
          rule,
          inputUnit: userTemperatureUnit === 'METRIC' ? 'c' : 'f',
          outputUnit: 'c',
        }),
      ),
      unit: rule.unit ?? null,
    })),
    sleepMinutes,
    startDate: null,
    stationGeoms: null,
  }

  if (startDate && endDate) {
    // for legacy reasons, the alert lambda codebase will necessitate this being here
    // This should become unnecessary when the lambda-alerts system is refactored
    variables.startDate = moment
      .tz(startDate, 'America/Los_Angeles')
      .startOf('day')
      .subtract(1, 'year')
      .toISOString()

    variables.endDate = moment
      .tz(endDate, 'America/Los_Angeles')
      .endOf('day')
      .subtract(1, 'year')
      .toISOString()
  }

  if (coverageType === 'properties' && coverageProperties && coverageProperties.length) {
    variables.propertyIds = coverageProperties
  }

  if (
    (coverageType === 'stationsOrBlocks' && coverageStations && coverageStations.length) ||
    (coverageBlocks && coverageBlocks.length)
  ) {
    let propertiesAndStations = coverageStations.map((v) => v.split('&')) // will return something like [50617, 0101000020E6100000B98C9B1A68085EC05DA9674128754240]
    let propertiesAndBlocks = coverageBlocks.map((v) => v.split('&')) // will return something like [50930, 16672]

    variables.stationGeoms = [...new Set(propertiesAndStations.map(([, stationId]) => stationId))]

    variables.blockIds = [...new Set(propertiesAndBlocks.map(([, blockId]) => +blockId))]

    variables.propertyIds = [
      ...new Set(propertiesAndStations.concat(propertiesAndBlocks).map(([propertyId]) => +propertyId)),
    ]
  }

  return variables
}
